import { type PropsWithChildren, useMemo } from "react";

import { TRANSITION_EASINGS } from "@heroui/framer-utils";
import {
	Drawer,
	DrawerBody,
	DrawerContent,
	type ModalProps,
	cn,
} from "@heroui/react";

import { SIDEBAR_WIDTH } from "@onesolution/constants";

type SidebarDrawerProps = ModalProps & {
	isCollapsed: boolean;
};

export default function SidebarDrawer({
	children,
	isCollapsed,
	isOpen,
	onOpenChange,
	...props
}: PropsWithChildren<SidebarDrawerProps>) {
	const motionProps = useMemo(
		() => ({
			variants: {
				enter: {
					x: 0,
					transition: {
						x: {
							duration: 0.3,
							ease: TRANSITION_EASINGS.easeOut,
						},
					},
				},
				exit: {
					x: -SIDEBAR_WIDTH,
					transition: {
						x: {
							duration: 0.2,
							ease: TRANSITION_EASINGS.easeOut,
						},
					},
				},
			},
		}),
		[],
	);
	return (
		<>
			<Drawer
				{...props}
				hideCloseButton
				placement="left"
				classNames={{
					wrapper: `!w-[${SIDEBAR_WIDTH}px] scrollbar-hide`,
					base: cn(
						`w-[${SIDEBAR_WIDTH}px] min-w-[288px] h-full max-h-full max-h-none rounded-lg scrollbar-hide`,
						{
							"min-w-[76px]": isCollapsed,
						},
					),
				}}
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				motionProps={motionProps}
				radius="none"
				scrollBehavior="inside"
			>
				<DrawerContent>
					<DrawerBody>{children}</DrawerBody>
				</DrawerContent>
			</Drawer>

			<div
				className={cn(
					`invisible sm:visible h-full max-w-[${SIDEBAR_WIDTH}px] overflow-x-hidden overflow-y-scroll min-w-[${SIDEBAR_WIDTH}px] rounded-lg scrollbar-hide`,
					{ "min-w-20": isCollapsed },
				)}
			>
				{children}
			</div>
		</>
	);
}
